/**
 * Converting notes, files and projects to and from end-to-end encryption. All encryption and
 * decryption happens here in the browser; the server receives ciphertext (or, when the owner
 * turns encryption off, the plaintext they decrypted) in one atomic request per note.
 */
import * as Y from "yjs";
import { KDF_ITERATIONS, generateScopeKey, keyProof, wrapScopeKey, type MemberSlotData } from "@brainlog/shared";
import { api, get, post } from "@/lib/api";
import { peekEntry, releaseCollab } from "@/features/notes/collab";
import { getScopeKey, rememberScopeKey, type ScopeInfo } from "./keyring";
import {
  decAnchor,
  decComment,
  decSnapshot,
  decTitle,
  decUpdate,
  decVersion,
  decryptFileBytes,
  decryptFileMeta,
  encAnchor,
  encComment,
  encSnapshot,
  encTitle,
  encVersion,
  encryptFile,
  requireKey,
  scopeOfFile,
  sha256Hex,
} from "./ops";

export interface NewScope {
  id: string;
  raw: Uint8Array;
  keyProof: string;
  slots: Awaited<ReturnType<typeof wrapScopeKey>>[];
  /** The key wrapped for members' personal keys (see ./personal). */
  memberSlots?: MemberSlotData[];
}

/** A fresh key wrapped by the passphrase (and the recovery key, when one was generated). */
export async function buildScope(passphrase: string, recoveryKey: string | null): Promise<NewScope> {
  const id = crypto.randomUUID();
  const { raw } = await generateScopeKey();
  const slots = [await wrapScopeKey(raw, passphrase, id, "passphrase", KDF_ITERATIONS)];
  if (recoveryKey) slots.push(await wrapScopeKey(raw, recoveryKey, id, "recovery", KDF_ITERATIONS));
  return { id, raw, keyProof: await keyProof(raw, id), slots };
}

export const scopePayload = (s: NewScope) => ({ id: s.id, keyProof: s.keyProof, slots: s.slots, memberSlots: s.memberSlots ?? [] });

function form(payload: unknown, files: [string, Blob][] = []) {
  const f = new FormData();
  f.append("payload", new Blob([JSON.stringify(payload)], { type: "application/json" }), "payload.json");
  for (const [name, blob] of files) f.append(name, blob, "blob");
  return f;
}

async function download(attId: string) {
  const res = await fetch(`/api/attachments/${attId}/file?download=1`, { credentials: "same-origin" });
  if (!res.ok) throw new Error("A file of this note could not be read. Please try again.");
  return new Uint8Array(await res.arrayBuffer());
}

interface FileRow {
  id: string;
  filename: string;
  mime: string;
  sizeBytes?: number;
  size?: number;
  encrypted: boolean;
  encMeta: string | null;
  owner?: { type: string; id: string } | null;
}

/** The note's own (non-deleted) files, as the server converts them — also for notes in the trash. */
async function ownedFiles(noteId: string): Promise<FileRow[]> {
  return (await get<{ files: FileRow[] }>(`/api/notes/${noteId}/owned-files`)).files;
}

export type Progress = (message: string) => void;

/**
 * Encrypt an existing plaintext note: its title, text, every version, comments and its own files.
 * `scope` is either a brand-new key for this note, or the project's (already unlocked) key.
 */
export async function encryptExistingNote(noteId: string, scope: { newScope: NewScope } | { scopeId: string }, progress: Progress = () => {}) {
  if (peekEntry(noteId)?.hasUnsynced()) throw new Error("This note still has edits that are not saved yet. Wait until it shows “Saved”, then try again.");
  const scopeId = "newScope" in scope ? scope.newScope.id : scope.scopeId;
  if ("newScope" in scope) await rememberScopeKey(scope.newScope.id, scope.newScope.raw);
  requireKey(scopeId);
  progress("Reading the note…");
  const d = await get<any>(`/api/notes/${noteId}`);
  const text: string = d.note.contentMd;
  const doc = new Y.Doc();
  doc.getText("content").insert(0, text);
  const state = Y.encodeStateAsUpdate(doc);
  doc.destroy();

  progress("Encrypting the version history…");
  const versions = [];
  for (const v of (await get<{ versions: { id: string }[] }>(`/api/notes/${noteId}/versions`)).versions) {
    const full = (await get<{ version: { title: string; contentMd: string } }>(`/api/notes/${noteId}/versions/${v.id}`)).version;
    versions.push({ id: v.id, blob: await encVersion(scopeId, noteId, v.id, { title: full.title, text: full.contentMd }) });
  }
  progress("Encrypting comments…");
  const comments = [];
  for (const c of (await get<{ comments: { id: string; body: string; anchorQuote: string | null }[] }>(`/api/notes/${noteId}/comments`)).comments) {
    comments.push({ id: c.id, body: await encComment(scopeId, noteId, c.id, c.body), anchor: c.anchorQuote ? await encAnchor(scopeId, noteId, c.id, c.anchorQuote) : null });
  }
  const files: { attachmentId: string; encMeta: string }[] = [];
  const parts: [string, Blob][] = [];
  const own = (await ownedFiles(noteId)).filter((f) => !f.encrypted);
  for (const [i, f] of own.entries()) {
    progress(`Encrypting file ${i + 1} of ${own.length}…`);
    const data = await download(f.id);
    const enc = await encryptFile(scopeId, data, { filename: f.filename, mime: f.mime, size: data.length });
    files.push({ attachmentId: f.id, encMeta: enc.encMeta });
    parts.push([`file:${f.id}`, enc.blob]);
  }
  const payload = {
    ...("newScope" in scope ? { scope: scopePayload(scope.newScope) } : { scopeId, keyProof: requireKey(scopeId).proof }),
    plainSha256: await sha256Hex(text),
    encTitle: await encTitle(scopeId, noteId, d.note.title),
    snapshot: await encSnapshot(scopeId, noteId, state),
    versions,
    comments,
    files,
  };
  progress("Saving the encrypted note…");
  await api(`/api/notes/${noteId}/encrypt`, { form: form(payload, parts) });
  releaseCollab(noteId);
  return { scopeId };
}

/** Full decrypted text of an encrypted note and the sequence number it corresponds to. */
export async function readEncryptedText(noteId: string, scopeId: string) {
  const st = await get<{ seq: number; snapshot: { blob: string } | null; updates: { blob: string }[] }>(`/api/notes/${noteId}/enc-state`);
  const doc = new Y.Doc();
  if (st.snapshot) Y.applyUpdate(doc, await decSnapshot(scopeId, noteId, st.snapshot.blob));
  let skipped = 0;
  for (const u of st.updates) {
    try {
      Y.applyUpdate(doc, await decUpdate(scopeId, noteId, u.blob));
    } catch {
      skipped++; // not made with this key (or damaged): it carries no readable text
    }
  }
  const text = doc.getText("content").toString();
  doc.destroy();
  return { text, seq: st.seq };
}

/** Turn encryption off for one note (the server receives the decrypted content). */
export async function decryptNote(noteId: string, progress: Progress = () => {}) {
  if (peekEntry(noteId)?.hasUnsynced()) throw new Error("This note still has edits that are not saved yet. Wait until it shows “Saved”, then try again.");
  const d = await get<any>(`/api/notes/${noteId}`);
  const scopeId: string = d.note.encryptionScopeId;
  if (!scopeId) return;
  requireKey(scopeId);
  progress("Decrypting the note…");
  const { text, seq } = await readEncryptedText(noteId, scopeId);
  const title = await decTitle(scopeId, noteId, d.note.encTitle);
  progress("Decrypting the version history…");
  const versions = [];
  for (const v of (await get<{ versions: { id: string }[] }>(`/api/notes/${noteId}/versions`)).versions) {
    const full = (await get<{ version: { contentMd: string } }>(`/api/notes/${noteId}/versions/${v.id}`)).version;
    const dv = await decVersion(scopeId, noteId, v.id, full.contentMd);
    versions.push({ id: v.id, title: dv.title || title, contentMd: dv.text });
  }
  const comments = [];
  for (const c of (await get<{ comments: { id: string; body: string; anchorQuote: string | null }[] }>(`/api/notes/${noteId}/comments`)).comments) {
    comments.push({ id: c.id, body: await decComment(scopeId, noteId, c.id, c.body), anchorQuote: c.anchorQuote ? await decAnchor(scopeId, noteId, c.id, c.anchorQuote) : null });
  }
  const files: { attachmentId: string; filename: string; mime: string }[] = [];
  const parts: [string, Blob][] = [];
  const own = (await ownedFiles(noteId)).filter((f) => f.encrypted && f.encMeta);
  for (const [i, f] of own.entries()) {
    progress(`Decrypting file ${i + 1} of ${own.length}…`);
    const meta = await decryptFileMeta(f.encMeta!);
    const plain = await decryptFileBytes(f.encMeta!, await download(f.id));
    files.push({ attachmentId: f.id, filename: meta.filename, mime: meta.mime });
    parts.push([`file:${f.id}`, new Blob([plain as BlobPart], { type: meta.mime })]);
  }
  progress("Saving the decrypted note…");
  await api(`/api/notes/${noteId}/decrypt`, {
    form: form({ keyProof: requireKey(scopeId).proof, seq, title, text, versions, comments, files }, parts),
  });
  releaseCollab(noteId);
}

/** Encrypt (or decrypt, for turning project encryption off) a single file in place. */
export async function convertFile(file: FileRow, direction: "encrypt" | "decrypt", scopeId: string) {
  const data = await download(file.id);
  if (direction === "encrypt") {
    const enc = await encryptFile(scopeId, data, { filename: file.filename, mime: file.mime, size: data.length });
    await api(`/api/files/${file.id}/content`, { method: "PUT", form: form({ keyProof: requireKey(scopeId).proof, encrypted: true, encMeta: enc.encMeta }, [["file:content", enc.blob]]) });
  } else {
    const meta = await decryptFileMeta(file.encMeta!);
    const plain = await decryptFileBytes(file.encMeta!, data);
    await api(`/api/files/${file.id}/content`, {
      method: "PUT",
      form: form({ keyProof: requireKey(scopeId).proof, encrypted: false, filename: meta.filename, mime: meta.mime }, [
        ["file:content", new Blob([plain as BlobPart], { type: meta.mime })],
      ]),
    });
  }
}

export interface EncryptionStatus {
  workspaceScopeId: string | null;
  scopes: ScopeInfo[];
  pending: { notes: { id: string; title: string; deleted?: boolean }[]; files: { id: string; filename: string }[]; encryptedNotes: number; encryptedFiles: number };
  /** Notes/files still under a former project key while encryption is being turned off. */
  decrypting: { scopeId: string; notes: string[]; files: string[] } | null;
}

/** Encrypt everything in a project that is not encrypted yet (resumable). */
export async function continueProjectEncryption(workspaceId: string, progress: Progress = () => {}) {
  let status = await get<EncryptionStatus>(`/api/workspaces/${workspaceId}/encryption`);
  const scopeId = status.workspaceScopeId;
  if (!scopeId) throw new Error("This project is not encrypted.");
  requireKey(scopeId);
  const notes = status.pending.notes;
  for (const [i, n] of notes.entries()) {
    progress(`Encrypting note ${i + 1} of ${notes.length}…`);
    await encryptExistingNote(n.id, { scopeId });
  }
  status = await get<EncryptionStatus>(`/api/workspaces/${workspaceId}/encryption`);
  const all = await get<{ files: FileRow[] }>(`/api/workspaces/${workspaceId}/files`);
  const pendingIds = new Set(status.pending.files.map((f) => f.id));
  const files = (all.files ?? []).filter((f) => pendingIds.has(f.id));
  for (const [i, f] of files.entries()) {
    progress(`Encrypting file ${i + 1} of ${files.length}…`);
    await convertFile(f, "encrypt", scopeId);
  }
  return get<EncryptionStatus>(`/api/workspaces/${workspaceId}/encryption`);
}

export async function enableProjectEncryption(workspaceId: string, scope: NewScope, progress: Progress = () => {}) {
  progress("Creating the project key…");
  await post(`/api/workspaces/${workspaceId}/encryption`, { scope: scopePayload(scope) });
  await rememberScopeKey(scope.id, scope.raw);
  return continueProjectEncryption(workspaceId, progress);
}

/**
 * Turn project encryption off (owner): the project stops being encrypted, then every note and file
 * still under the former project key is decrypted here. Resumable via `finishProjectDecryption`.
 */
export async function disableProjectEncryption(workspaceId: string, progress: Progress = () => {}) {
  const status = await get<EncryptionStatus>(`/api/workspaces/${workspaceId}/encryption`);
  const scopeId = status.workspaceScopeId;
  if (!scopeId) return finishProjectDecryption(workspaceId, progress);
  requireKey(scopeId);
  progress("Turning off encryption for new content…");
  await post(`/api/workspaces/${workspaceId}/encryption/disable`, { keyProof: requireKey(scopeId).proof });
  return finishProjectDecryption(workspaceId, progress);
}

export async function finishProjectDecryption(workspaceId: string, progress: Progress = () => {}) {
  const status = await get<EncryptionStatus>(`/api/workspaces/${workspaceId}/encryption`);
  const left = status.decrypting;
  if (!left) return;
  requireKey(left.scopeId);
  for (const [i, id] of left.notes.entries()) {
    progress(`Decrypting note ${i + 1} of ${left.notes.length}…`);
    await decryptNote(id);
  }
  const all = await get<{ files: FileRow[] }>(`/api/workspaces/${workspaceId}/files`);
  const files = (all.files ?? []).filter((f) => left.files.includes(f.id));
  for (const [i, f] of files.entries()) {
    progress(`Decrypting file ${i + 1} of ${files.length}…`);
    await convertFile(f, "decrypt", left.scopeId);
  }
}

export const hasKey = (scopeId: string | null | undefined) => !!getScopeKey(scopeId);
